import { zipSync } from "fflate";

/** Run async work with a fixed concurrency limit (preserves result order). */
export async function mapWithConcurrency<T, R>(
  items: T[],
  limit: number,
  fn: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  if (!items.length) return [];
  const results = new Array<R>(items.length);
  let nextIndex = 0;
  const workers = Math.max(1, Math.min(limit, items.length));

  async function worker(): Promise<void> {
    for (;;) {
      const i = nextIndex;
      nextIndex += 1;
      if (i >= items.length) return;
      results[i] = await fn(items[i], i);
    }
  }

  await Promise.all(Array.from({ length: workers }, () => worker()));
  return results;
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename.replace(/\.zip$/i, "").trim() || "download";
    if (!/\.zip$/i.test(a.download)) a.download += ".zip";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function blobToUint8Array(blob: Blob): Promise<Uint8Array> {
  return new Uint8Array(await blob.arrayBuffer());
}

/** Build a ZIP blob from named file entries (paths use forward slashes). */
export async function buildZipBlob(files: Array<{ path: string; blob: Blob }>): Promise<Blob> {
  const zipEntries: Record<string, Uint8Array> = {};
  for (const file of files) {
    const path = file.path.replace(/\\/g, "/").replace(/^\/+/, "");
    if (!path) continue;
    zipEntries[path] = await blobToUint8Array(file.blob);
  }
  if (!Object.keys(zipEntries).length) throw new Error("No files to zip.");
  const zipped = zipSync(zipEntries);
  return new Blob([zipped], { type: "application/zip" });
}

export async function downloadZip(files: Array<{ path: string; blob: Blob }>, zipBasename: string): Promise<void> {
  const blob = await buildZipBlob(files);
  downloadBlob(blob, zipBasename);
}
